import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, isSameDay, addMonths, subMonths } from 'date-fns'
import { sv } from 'date-fns/locale'
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  Button,
  Badge,
  Modal,
  Input,
  Select,
} from '@besiktning/ui'
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Clock, Plus } from 'lucide-react'
import { garageApi, carCareApi, formatDateTime, formatCurrency, formatInTz } from '@besiktning/ui'
import type { Booking, Service } from '@besiktning/ui'
import { useAuthStore } from '../store/authStore'
import { t } from '../i18n'

// A customer who phoned in: staff enter contact details, the car and the
// service, and the job is booked straight away (paid at the garage).
function NewBookingModal({ isOpen, onClose, defaultDate }: { isOpen: boolean; onClose: () => void; defaultDate: Date | null }) {
  const queryClient = useQueryClient()
  const blank = () => ({
    name: '', phone: '', email: '', reg_number: '', service_id: '', notes: '',
    // Zoneless wall-clock; Rails reads it in the brand's time zone.
    scheduled_at: format(defaultDate ?? new Date(), "yyyy-MM-dd'T'09:00"),
  })
  const [form, setForm] = useState(blank)
  const [error, setError] = useState<string | null>(null)
  const set = (key: keyof ReturnType<typeof blank>) =>
    (e: { target: { value: string } }) => setForm((f) => ({ ...f, [key]: e.target.value }))

  const { data: services = [] } = useQuery({
    queryKey: ['services', 'garage'],
    queryFn: () => garageApi.getServices(),
    enabled: isOpen,
  })
  const activeServices = (services as Service[]).filter((sv) => sv.active)

  const create = useMutation({
    mutationFn: () => garageApi.createBooking({
      service_id: form.service_id,
      scheduled_at: form.scheduled_at,
      reg_number: form.reg_number,
      notes: form.notes,
      customer: { name: form.name, phone: form.phone, email: form.email },
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] })
      queryClient.invalidateQueries({ queryKey: ['jobs'] })
      setForm(blank())
      setError(null)
      onClose()
    },
    onError: (err: any) => setError(err?.response?.data?.error ?? t('calendar.newBookingFailed')),
  })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    create.mutate()
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('calendar.newBooking')} description={t('calendar.newBookingHint')} size="lg">
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input label={t('calendar.fieldName')} value={form.name} onChange={set('name')} required autoComplete="off" />
          <Input label={t('calendar.fieldPhone')} type="tel" value={form.phone} onChange={set('phone')} required />
          <Input label={t('calendar.fieldEmail')} type="email" value={form.email} onChange={set('email')} />
          <Input label={t('calendar.fieldReg')} value={form.reg_number} onChange={set('reg_number')} required
            className="uppercase" />
        </div>
        <Select label={t('calendar.fieldService')} value={form.service_id} onChange={set('service_id')} required
          options={[
            { value: '', label: t('calendar.fieldServicePlaceholder') },
            ...activeServices.map((sv) => ({
              value: String(sv.id),
              label: sv.price != null ? `${sv.name} – ${formatCurrency(sv.price)}` : sv.name,
            })),
          ]} />
        <Input label={t('calendar.fieldTime')} type="datetime-local" value={form.scheduled_at} onChange={set('scheduled_at')} required />
        <Input label={t('calendar.fieldNotes')} value={form.notes} onChange={set('notes')} />
        {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>{t('calendar.cancel')}</Button>
          <Button type="submit" disabled={create.isPending}>{t('calendar.createBooking')}</Button>
        </div>
      </form>
    </Modal>
  )
}

export default function Calendar() {
  const { currentMembership } = useAuthStore()
  const isCarCare = currentMembership?.type === 'car_care'
  const api = isCarCare ? carCareApi : garageApi
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [showNewBooking, setShowNewBooking] = useState(false)

  const { data: bookings = [] } = useQuery({
    queryKey: ['bookings', isCarCare ? 'car_care' : 'garage', currentMonth],
    queryFn: () => {
      const start = startOfMonth(currentMonth)
      const end = endOfMonth(currentMonth)
      return api.getBookings({
        from: start.toISOString(),
        to: end.toISOString(),
      })
    },
  })

  const monthStart = startOfMonth(currentMonth)
  const monthEnd = endOfMonth(currentMonth)
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd })

  // Get bookings for a specific day
  const getBookingsForDay = (day: Date) => {
    return bookings.filter((booking: Booking) => {
      if (!booking.scheduled_at) return false
      return isSameDay(new Date(booking.scheduled_at), day)
    })
  }

  // Navigate months
  const nextMonth = () => setCurrentMonth(addMonths(currentMonth, 1))
  const prevMonth = () => setCurrentMonth(subMonths(currentMonth, 1))
  const today = () => setCurrentMonth(new Date())

  // Get day of week for first day (0 = Sunday, 1 = Monday, etc.)
  const startingDayIndex = monthStart.getDay()
  // Adjust for Monday start (0 = Monday in our grid)
  const adjustedStartIndex = startingDayIndex === 0 ? 6 : startingDayIndex - 1

  return (
    <div>
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('calendar.title')}</h1>
          <p className="text-gray-600">
            {t('calendar.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!isCarCare && (
            <Button size="sm" onClick={() => setShowNewBooking(true)}>
              <Plus className="mr-1 h-4 w-4" /> {t('calendar.newBooking')}
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={today}>
            {t('calendar.today')}
          </Button>
          <div className="flex items-center bg-white rounded-lg border border-gray-200">
            <button
              onClick={prevMonth}
              className="p-2 hover:bg-gray-100 rounded-l-lg"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="px-4 font-medium min-w-[140px] text-center">
              {format(currentMonth, 'MMMM yyyy', { locale: sv })}
            </span>
            <button
              onClick={nextMonth}
              className="p-2 hover:bg-gray-100 rounded-r-lg"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar Grid */}
        <Card className="lg:col-span-2">
          <CardContent className="p-4">
            {/* Day headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {[t('calendar.dayMon'), t('calendar.dayTue'), t('calendar.dayWed'), t('calendar.dayThu'), t('calendar.dayFri'), t('calendar.daySat'), t('calendar.daySun')].map((day) => (
                <div
                  key={day}
                  className="text-center text-sm font-medium text-gray-500 py-2"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar days */}
            <div className="grid grid-cols-7 gap-1">
              {/* Empty cells for days before month starts */}
              {Array.from({ length: adjustedStartIndex }).map((_, index) => (
                <div key={`empty-${index}`} className="aspect-square" />
              ))}

              {/* Days of the month */}
              {daysInMonth.map((day) => {
                const dayBookings = getBookingsForDay(day)
                const isSelected = selectedDate && isSameDay(day, selectedDate)
                const isCurrentMonth = isSameMonth(day, currentMonth)
                const isTodayDate = isToday(day)

                return (
                  <button
                    key={day.toISOString()}
                    onClick={() => setSelectedDate(day)}
                    className={`
                      aspect-square p-2 rounded-lg border text-left transition-colors relative
                      ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-100 hover:bg-gray-50'}
                      ${isTodayDate ? 'ring-2 ring-blue-500 ring-offset-1' : ''}
                      ${!isCurrentMonth ? 'text-gray-300' : ''}
                    `}
                  >
                    <span className={`text-sm font-medium ${isTodayDate ? 'text-blue-600' : ''}`}>
                      {format(day, 'd')}
                    </span>
                    {dayBookings.length > 0 && (
                      <div className="absolute bottom-1 left-1 right-1 flex gap-0.5">
                        {dayBookings.slice(0, 3).map((_: unknown, i: number) => (
                          <div
                            key={i}
                            className="h-1.5 w-1.5 rounded-full bg-blue-500"
                          />
                        ))}
                        {dayBookings.length > 3 && (
                          <div className="h-1.5 w-1.5 rounded-full bg-gray-300" />
                        )}
                      </div>
                    )}
                  </button>
                )
              })}
            </div>
          </CardContent>
        </Card>

        {/* Selected Day Bookings */}
        <Card>
          <CardHeader>
            <CardTitle>
              {selectedDate
                ? format(selectedDate, 'EEEE d MMMM', { locale: sv })
                : t('calendar.selectDate')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {selectedDate ? (
              <div className="space-y-4">
                {getBookingsForDay(selectedDate).length > 0 ? (
                  getBookingsForDay(selectedDate).map((booking: Booking) => (
                    <Link
                      key={booking.id}
                      to={`/jobs/${booking.id}`}
                      className="block p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium">
                            {booking.user?.name || t('calendar.customer')}
                          </p>
                          <p className="text-sm text-gray-500">
                            {booking.title}
                          </p>
                          <p className="text-xs text-gray-400">
                            {(booking.vehicle ?? booking.quote?.inspection_request?.vehicle)?.reg_number}
                          </p>
                        </div>
                        <Badge variant={booking.status === 'confirmed' ? 'success' : 'default'}>
                          {booking.status === 'confirmed' ? t('calendar.statusConfirmed') : booking.status}
                        </Badge>
                      </div>
                      <div className="mt-3 flex items-center text-sm text-gray-600">
                        <Clock className="mr-1 h-4 w-4" />
                        {booking.scheduled_at &&
                          formatInTz(booking.scheduled_at, { hour: '2-digit', minute: '2-digit' })}
                      </div>
                      {booking.quote?.price && (
                        <p className="mt-2 text-sm font-medium">
                          {formatCurrency(booking.quote.price)}
                        </p>
                      )}
                    </Link>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <CalendarIcon className="mx-auto h-12 w-12 text-gray-300 mb-2" />
                    <p>{t('calendar.noBookingsForDate')}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <CalendarIcon className="mx-auto h-12 w-12 text-gray-300 mb-2" />
                <p>{t('calendar.selectDatePrompt')}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Upcoming Bookings List */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>{t('calendar.upcomingBookings')}</CardTitle>
        </CardHeader>
        <CardContent>
          {bookings
            .filter((b: Booking) => b.scheduled_at && new Date(b.scheduled_at) >= new Date())
            .sort((a: Booking, b: Booking) => 
              new Date(a.scheduled_at!).getTime() - new Date(b.scheduled_at!).getTime()
            )
            .slice(0, 5)
            .map((booking: Booking) => (
              <Link
                key={booking.id}
                to={`/jobs/${booking.id}`}
                className="flex items-center justify-between py-3 px-2 -mx-2 rounded-lg border-b border-gray-100 last:border-0 hover:bg-gray-50"
              >
                <div className="flex items-center">
                  <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <CalendarIcon className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="ml-3">
                    <p className="font-medium">
                      {booking.title}{' '}
                      <span className="text-gray-500 font-normal">
                        {(booking.vehicle ?? booking.quote?.inspection_request?.vehicle)?.reg_number}
                      </span>
                    </p>
                    <p className="text-sm text-gray-500">
                      {booking.user?.name || t('calendar.customer')}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-medium">
                    {booking.scheduled_at &&
                      formatDateTime(booking.scheduled_at)}
                  </p>
                  <p className="text-sm text-gray-500">
                    {booking.quote?.price && formatCurrency(booking.quote.price)}
                  </p>
                </div>
              </Link>
            ))}
        </CardContent>
      </Card>

      {!isCarCare && (
        <NewBookingModal isOpen={showNewBooking} onClose={() => setShowNewBooking(false)} defaultDate={selectedDate} />
      )}
    </div>
  )
}
